import React from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";

import { ComponentColor, ComponentSize } from "./types";

export const Button = ({
    children,
    className,
    type = "button",
    color = ComponentColor.Neutral,
    shape = "default",
    size = ComponentSize.MD,
    outline,
    wide,
    loading,
    disabled,
    noAnimation,
    onClick,
    to,
    href,
    ...passThrough
}: ButtonProps): React.JSX.Element => {
    const buttonClasses = classNames(
        "btn",
        color === ComponentColor.Accent ? "btn-accent" : "",
        color === ComponentColor.Error ? "btn-error" : "",
        color === ComponentColor.Ghost ? "btn-ghost" : "",
        color === ComponentColor.Info ? "btn-info" : "",
        color === "link" ? "btn-link" : "",
        color === ComponentColor.Neutral ? "btn-neutral" : "",
        color === ComponentColor.Primary ? "btn-primary" : "",
        color === ComponentColor.Secondary ? "btn-secondary" : "",
        color === ComponentColor.Success ? "btn-success" : "",
        color === ComponentColor.Warning ? "btn-warning" : "",
        shape === "square" ? "btn-square" : "",
        shape === "circle" ? "btn-circle" : "",
        size === ComponentSize.XL ? "btn-xl" : "",
        size === ComponentSize.LG ? "btn-lg" : "",
        size === ComponentSize.MD ? "btn-md" : "",
        size === ComponentSize.SM ? "btn-sm" : "",
        size === ComponentSize.XS ? "btn-xs" : "",
        size === "wide" ? "btn-wide" : "",
        size === "block" ? "btn-block" : "",
        outline ? "btn-outline" : "",
        wide ? "btn-wide" : "w-fit",
        noAnimation ? "no-animation" : "",
        // TODO: disabled or btn-disable hide with style the buttom making invisble
        disabled || loading
            ? "!bg-gray-300 !text-gray-600 !border-gray-300"
            : "",
        className,
    );

    // Anchors and links can't be natively `disabled`; mirror the visual state
    // with aria-disabled + the existing disabled classes and skip navigation.
    const isDisabled = disabled || loading;

    if (to) {
        return (
            <Link
                {...passThrough}
                to={to}
                className={buttonClasses}
                onClick={(event) => {
                    if (isDisabled) {
                        event.preventDefault();
                        return;
                    }
                    onClick?.(event);
                }}
                aria-disabled={isDisabled || undefined}
            >
                {children}
            </Link>
        );
    }

    if (href) {
        return (
            <a
                {...passThrough}
                href={href}
                className={buttonClasses}
                onClick={(event) => {
                    if (isDisabled) {
                        event.preventDefault();
                        return;
                    }
                    onClick?.(event);
                }}
                aria-disabled={isDisabled || undefined}
            >
                {children}
            </a>
        );
    }

    return (
        <button
            {...passThrough}
            type={type}
            className={buttonClasses}
            onClick={onClick}
            disabled={disabled || loading}
        >
            {loading && <span className="loading loading-spinner"></span>}
            {children}
        </button>
    );
};

// The aria-* and data-* attributes, forwarded as-is to whichever element
// renders. TypeScript exempts hyphenated JSX attributes from excess-property
// checks, so an attribute Button did not forward compiled and vanished (#405).
// aria-disabled is the one exception; ButtonProps rejects it.
type PassThroughAttributes = React.AriaAttributes & {
    [dataAttribute: `data-${string}`]: string | number | boolean | undefined;
};

interface ButtonProps extends PassThroughAttributes {
    // Not a prop: `disabled` is the only way to disable a Button. A caller's
    // aria-disabled would mark a native button disabled while its click still
    // fires, and in link and anchor mode Button sets its own from `disabled`
    // and `loading`, to match the navigation it blocks.
    "aria-disabled"?: never;
    children: React.ReactNode;
    className?: string;
    type?: ButtonType;
    color?: ComponentColor | "link";
    size?: ComponentSize | "wide" | "block";
    shape?: ButtonShape;
    outline?: boolean;
    wide?: boolean;
    disabled?: boolean;
    loading?: boolean;
    noAnimation?: boolean;
    onClick?: React.MouseEventHandler<HTMLElement>;
    /** Render a React Router `<Link to={to}>` instead of a `<button>`. */
    to?: string;
    /** Render a plain `<a href={href}>` instead of a `<button>`. */
    href?: string;
}

type ButtonType = "button" | "submit" | "reset";

type ButtonShape = "default" | "square" | "circle";

export default Button;
